/**
 * vinesPipeline — climbing vines around an unseen cylindrical trunk.
 *
 * 6 independent vine strands spiral around a hidden central trunk.
 * Each strand is a smooth parametric helix with its own phase, rate,
 * radius and color. Along each strand, leaves are placed at fixed
 * arc-length intervals; each leaf has a stem-vein SDF that pulses on
 * mid-band audio and a translucent petal lobe that brightens with
 * the bass.
 *
 * The tight spiral means each strand wraps many times — the
 * visualisation is dominated by spirals + leaves, never a blank
 * background.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface VinesPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// --- hashes & noises ---
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise2(p);
    p *= 2.03; a *= 0.5;
  }
  return v;
}

// 2D rotation.
mat2 rot(float a) {
  float c = cos(a), s = sin(a);
  return mat2(c, s, -s, c);
}

// Distance from q to line segment a-b.
float distToSeg(vec2 q, vec2 a, vec2 b) {
  vec2 ab = b - a;
  vec2 aq = q - a;
  float t = clamp(dot(aq, ab) / max(dot(ab, ab), 1e-5), 0.0, 1.0);
  return length(q - (a + ab * t));
}

// Ellipse SDF.
float sdEllipse(vec2 p, vec2 ab) {
  float k0 = length(p / ab);
  float k1 = length(p / (ab * ab));
  return k0 * (k0 - 1.0) / max(k1, 1e-5);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  // Audio.
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime;

  // Background: midnight forest with soft radial glow from a moon.
  vec3 sky = vec3(0.025, 0.045, 0.060);
  vec3 moon = vec3(0.50, 0.55, 0.70);
  float moonRadius = 0.18;
  vec2 moonPos = vec2(0.35, 0.30);
  vec2 mp = p - moonPos;
  float moonGlow = exp(-dot(mp, mp) * 4.0);
  vec3 col = sky + moon * moonGlow * 0.10;

  // Distant forest depth: subtle horizontal bands.
  float band = fbm(vec2(p.x * 0.8 + t * 0.02, p.y * 1.2)) * 0.5
             + fbm(vec2(p.x * 0.4,             p.y * 0.6 + t * 0.05)) * 0.5;
  col += vec3(0.02, 0.04, 0.03) * band;

  // The hidden trunk: just a soft amber cylindrical glow down the center.
  float trunkDist = abs(p.x);
  float trunkMask = exp(-trunkDist * 18.0) * 0.10;
  col += vec3(0.40, 0.25, 0.10) * trunkMask;

  // 6 vine strands, each spiraling.
  for (int v = 0; v < 6; v++) {
    float fv = float(v);
    float phase0 = fv * 1.04;
    float spinRate = 0.45 + 0.18 * hash21(vec2(fv, 7.0));
    float radiusBase = 0.10 + 0.04 * hash21(vec2(fv, 11.0));
    float verticalRate = 0.55 + 0.20 * hash21(vec2(fv, 13.0));
    float twist = 5.0 + 2.0 * hash21(vec2(fv, 17.0));
    float baseY = -0.65 + 1.30 * hash21(vec2(fv, 23.0));

    // Vine color: leaf-green to magenta/gold mix.
    vec3 vineColA = vec3(0.10, 0.55, 0.20);
    vec3 vineColB = vec3(0.55, 0.05, 0.55);
    vec3 vineColC = vec3(0.85, 0.55, 0.10);
    vec3 vineCol = mix(vineColA, vineColB,
                       0.5 + 0.5 * sin(t * 0.4 + fv));
    vineCol = mix(vineCol, vineColC, high * 0.3);

    // Sample the helix as a polyline.
    const int SEG = 24;
    vec2 prev = vec2(0.0);
    bool firstIter = true;
    for (int s = 0; s < 25; s++) {
      float fs = float(s);
      float u = fs / float(SEG); // 0..1 along strand
      float theta = u * twist * 6.2832 + phase0 + t * spinRate;
      float r = radiusBase + 0.04 * sin(u * 6.0 + t * 0.5 + fv);
      r += bass * 0.020 * sin(u * 12.0 + t);
      vec2 pos = vec2(cos(theta) * r, baseY + u * 1.20);
      // Slight vertical wobble.
      pos.x += 0.015 * sin(t * 1.1 + u * 8.0 + fv);
      pos.y += 0.015 * cos(t * 0.7 + u * 9.0 + fv * 2.0);
      if (!firstIter) {
        float vd = distToSeg(p, prev, pos);
        float thick = 0.012 + bass * 0.004;
        float wire = smoothstep(thick + 0.004, thick - 0.003, vd);
        col = mix(col, vineCol, wire);
        // Inner highlight.
        float hl = smoothstep(thick * 0.5 + 0.002, thick * 0.5 - 0.001, vd);
        col += vineCol * hl * 0.4;
      }
      prev = pos;
      firstIter = false;

      // Place a leaf at every 4th segment.
      if (s % 4 == 0) {
        // Leaf is an ellipse rotated to face outward radially.
        vec2 outward = normalize(vec2(cos(theta), -sin(theta)) + vec2(0.0, 0.5));
        vec2 local = (p - pos) * rot(atan(outward.y, outward.x));
        float leafShape = 1.0 - sdEllipse(local, vec2(0.07, 0.025));
        leafShape = smoothstep(0.0, 0.05, leafShape);
        // Leaf body color.
        vec3 leafCol = mix(vineColA * 1.2, vineCol * 0.5, fract(u * 4.0 + fv * 0.1));
        // Leaf veins.
        float vein = abs(local.y) + abs(local.x * 4.0) * 0.0;
        float veinMask = (1.0 - smoothstep(0.0, 0.004, abs(local.y))) * leafShape;
        // Pulse on mid.
        float pulse = 0.5 + 0.5 * sin(t * (2.0 + fv * 0.4)
                                       + fs * 0.9 + fv * 7.0);
        float pulseGlow = (0.45 + 0.85 * pulse) * (0.5 + mid * 1.2)
                        * (0.6 + bass * 0.6) * leafShape;
        col = mix(col, leafCol, leafShape * 0.7);
        col += vec3(0.30, 0.75, 0.40) * veinMask * (0.5 + 0.7 * pulse);
        col += vineCol * pulseGlow * 0.6;
        // Highlight tip.
        float tipMask = (1.0 - smoothstep(0.0, 0.012, length(local + vec2(0.04, 0.0))))
                      * leafShape;
        col += vec3(0.85, 0.95, 0.55) * tipMask * (0.6 + 0.7 * pulse);
      }
    }
  }

  // Soft drifting pollen / fireflies.
  for (int i = 0; i < 30; i++) {
    float fi = float(i);
    vec2 flyPos = vec2(
      hash21(vec2(fi, 31.0)) * 2.0 - 1.0,
      hash21(vec2(fi, 47.0)) * 2.0 - 1.0
    ) * vec2(1.2, 0.9);
    flyPos.x *= uResolution.x / uResolution.y;
    flyPos += 0.15 * vec2(sin(t * 0.6 + fi), cos(t * 0.8 + fi * 1.3));
    float fd = length(p - flyPos);
    float tw = pow(0.5 + 0.5 * sin(t * (3.0 + fi * 0.2) + fi * 7.0), 8.0);
    col += vec3(0.85, 0.95, 0.45) * tw * exp(-fd * 50.0) * 0.8;
  }

  // Audio overall glow.
  col += vec3(0.10, 0.25, 0.15) * bass * 0.30
       + vec3(0.25, 0.20, 0.05) * mid  * 0.20
       + vec3(0.10, 0.20, 0.30) * high * 0.15;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeVinesBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.6);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.2));
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.9);
  out[3] = 0.5 + 0.4 * Math.sin(t * 0.9 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.5 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.2 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.5 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.2 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.0 + 2.1);
}

export function computeVinesRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createVinesPipeline(gl: WebGL2RenderingContext): VinesPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawVines(
  gl: WebGL2RenderingContext,
  pipe: VinesPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
